import { useQuestionHandlers } from "@/lib/dnd/hooks/useQuestionHandlers";
import { useCopyFromMediaHouse } from "@/lib/hooks/medias/useCopyFromMediaHouse";
import { withToast } from "@/lib/utils/withToast";
import type { DropCenter } from "./handlers/types";
import { useSlideHandlers } from "./handlers/useSlideHandlers";

/**
 * Unified media handler that abstracts the copy-from-media-house + add-to-slide workflow
 */
export const useMediaHandler = () => {
  const { addImage, addAudio, addVideo } = useSlideHandlers();
  const { handleQuestionDragAndDrop } = useQuestionHandlers();
  const { copyFromMediaHouse, copying } = useCopyFromMediaHouse();

  const addImageToSlide = async (
    mediaHouseId: string,
    containerId?: string,
    dropCenter?: DropCenter
  ) => {
    return withToast(
      async () => {
        if (!addImage) {
          throw new Error("Current slide type does not support images");
        }

        const mediaAsset = await copyFromMediaHouse(mediaHouseId);
        // Question elements own their drop targets on every slide type, so this has to be routed
        // here rather than inside a per-type handler: only the freeform one did, which left a drop
        // on a question in any other template overwriting the slide's own image.
        if (handleQuestionDragAndDrop(mediaAsset, containerId)) return;
        addImage(mediaAsset, containerId, dropCenter);
      },
      {
        loading: "Adding image...",
        success: "Image added!",
        error: (error) => (error instanceof Error ? error.message : "Failed to add image"),
      },
      "addImageToSlide"
    );
  };

  const addAudioToSlide = async (
    mediaHouseId: string,
    containerId?: string,
    phonemeId?: string,
    dropCenter?: DropCenter
  ) => {
    return withToast(
      async () => {
        if (!addAudio) {
          throw new Error("Current slide type does not support audio");
        }

        const mediaAsset = await copyFromMediaHouse(mediaHouseId, phonemeId);
        addAudio(mediaAsset, containerId, dropCenter);
      },
      {
        loading: "Adding audio...",
        success: "Audio added!",
        error: (error) => (error instanceof Error ? error.message : "Failed to add audio"),
      },
      "addAudioToSlide"
    );
  };

  const addVideoToSlide = async (
    mediaHouseId: string,
    containerId?: string,
    dropCenter?: DropCenter
  ) => {
    return withToast(
      async () => {
        if (!addVideo) {
          throw new Error("Current slide type does not support videos");
        }

        const mediaAsset = await copyFromMediaHouse(mediaHouseId);
        addVideo(mediaAsset, containerId, dropCenter);
      },
      {
        loading: "Adding video...",
        success: "Video added!",
        error: "Failed to add video",
      },
      "addVideoToSlide"
    );
  };

  return {
    addImageToSlide,
    addAudioToSlide,
    addVideoToSlide,
    copyFromMediaHouse,
    copying,
  };
};
